import Button from '@/app/_components/buttons/Button';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import Image from 'next/image';
import CloseIcon from '@/public/icons/close.svg';

interface Props {
  dialogKey?: string;
  isOpen: boolean;
  handleClose: () => void;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  primaryButtonLabel?: string;
  primaryButtonAction?: () => void;
  tertiaryButtonLabel?: string;
  tertiaryButtonAction?: () => void;
}

const InfoDialog = ({
  title,
  description,
  dialogKey,
  isOpen,
  handleClose,
  primaryButtonLabel,
  primaryButtonAction,
  tertiaryButtonLabel,
  tertiaryButtonAction,
  children,
}: Props) => (
  <Transition show={isOpen}>
    <Dialog
      as="div"
      className="absolute top-0 left-0 bottom-0 right-0 z-50 p-6 flex items-center justify-center min-h-full min-w-full h-screen"
      onClose={handleClose}
    >
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="ease-in opacity-0"
        enterTo="ease-out opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black/50 min-h-screen"></div>
      </Transition.Child>
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Dialog.Panel className="relative overflow-hidden rounded bg-white transition-all w-full md:w-1/2 lg:w-1/3 mt-10 md:m-10">
          <Dialog.Title
            as="div"
            className="text-xl font-hanken leading-normal text-purple-9 flex justify-between py-10 px-6"
          >
            <h3 className="text-2xl">{title}</h3>
            <button
              type="button"
              className="-m-3 p-3 focus-visible:outline-offset-[-4px] text-white"
              data-dd-action-name={`Close ${dialogKey} dialog`}
              onClick={handleClose}
              data-test-id={`close-${dialogKey}-dialog`}
            >
              <Image {...CloseIcon} />
            </button>
          </Dialog.Title>
          <div>
            <div className="pb-6 px-6">
              <div className="font-hanken font-normal text-base leading-normal text-left">
                {description}
                {children}
              </div>
            </div>
            <div className="flex flex-row justify-end gap-10 px-6 py-4">
              {tertiaryButtonLabel && (
                <Button
                  variant="tertiary"
                  label={tertiaryButtonLabel}
                  data-dd-action-name={`${dialogKey}-dialog-tertiary-btn-click`}
                  onClick={tertiaryButtonAction}
                  dataTestId={`${dialogKey}-dialog-tertiary-btn`}
                />
              )}
              {primaryButtonLabel && (
                <Button
                  variant="primary"
                  label={primaryButtonLabel}
                  data-dd-action-name={`${dialogKey}-dialog-primary-btn-click`}
                  onClick={primaryButtonAction}
                  dataTestId={`${dialogKey}-dialog-primary-btn`}
                />
              )}
            </div>
          </div>
        </Dialog.Panel>
      </Transition.Child>
    </Dialog>
  </Transition>
);

export default InfoDialog;
